import { isPlatformBrowser } from '@angular/common';
import { provideHttpClient, withFetch } from '@angular/common/http';
import {
  ApplicationConfig,
  inject,
  isDevMode,
  PLATFORM_ID,
  provideAppInitializer,
  provideBrowserGlobalErrorListeners,
} from '@angular/core';
import { provideClientHydration, withEventReplay } from '@angular/platform-browser';
import { provideRouter, Router, withInMemoryScrolling } from '@angular/router';
import { provideIcons } from '@ng-icons/core';
import { featherAirplay } from '@ng-icons/feather-icons';
import { heroUsers } from '@ng-icons/heroicons/outline';
import { QueryClient, provideTanStackQuery } from '@tanstack/angular-query-experimental';
import { withDevtools } from '@tanstack/angular-query-experimental/devtools';
import { providePrimeNG } from 'primeng/config';
import { PtAuthSessionService, registerUnauthorizedResponseHandler } from '@pt/services/auth';
import { LAYOUT_CONFIG } from '@pt/services/layout/layout.config';
import { ThemeService } from '@pt/services/theme/theme.service';
import { routes } from './app.routes';
import { appPrimeNgTheme } from './theme/theme';
import { TranslocoHttpLoader } from './transloco-loader';
import { provideTransloco } from '@jsverse/transloco';
import { provideTranslocoLocale } from '@jsverse/transloco-locale';

export const appConfig: ApplicationConfig = {
  providers: [
    // HTTP layer
    //
    // - `withFetch()` is SSR-friendly and aligns with Angular guidance.
    provideHttpClient(withFetch()),

    // TanStack Query
    //
    // We pin package versions to exact patch releases because Angular Query
    // is currently experimental and may ship breaking changes in patches.
    provideTanStackQuery(
      new QueryClient(),
      withDevtools(() => ({
        buttonPosition: 'bottom-right',
      })),
    ),

    providePrimeNG({
      theme: appPrimeNgTheme,
    }),

    provideIcons({
      featherAirplay,
      heroUsers,
    }),

    provideAppInitializer(() => {
      inject(ThemeService).initialize();
    }),

    provideAppInitializer(() => {
      const platformId = inject(PLATFORM_ID);
      if (!isPlatformBrowser(platformId)) {
        return;
      }

      const authSession = inject(PtAuthSessionService);
      const router = inject(Router);

      registerUnauthorizedResponseHandler(() => {
        authSession.handleUnauthorized();

        const currentUrl = router.url;
        if (currentUrl.startsWith('/sign-in')) {
          return;
        }

        void router.navigate(['/sign-in'], {
          queryParams: { redirectURL: currentUrl },
        });
      });
    }),

    {
      provide: LAYOUT_CONFIG,
      useValue: { defaultLayout: 'default' },
    },

    provideBrowserGlobalErrorListeners(),
    provideRouter(routes, withInMemoryScrolling({ scrollPositionRestoration: 'enabled' })),
    provideClientHydration(withEventReplay()),
    provideHttpClient(),
    provideTransloco({
      config: {
        availableLangs: ['en-US', 'sl-SI'],
        defaultLang: 'en-US',
        // Remove this option if your application doesn't support changing language in runtime.
        reRenderOnLangChange: true,
        prodMode: !isDevMode(),
      },
      loader: TranslocoHttpLoader,
    }),
    provideTranslocoLocale(),
  ],
};
